import { useQuery } from '@apollo/client';
import { Autocomplete, Box, FormControl, MenuItem, styled, TextField } from '@mui/material';
import Loader from 'Components/Forms/Loader';
import useDialog from 'hooks/useDialog';
import PaperWithAddItem from 'DesignSystem/Inputs/Paper/Paper';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import { GET_QUESTIONNAIRES } from 'graphql/secure/queries/questionnaire.queries';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  IBaseQuestionnaire,
  IQuestionnaire,
} from 'FeatureModules/Questionnaire/types/questionnaire.types';
import { IGraphQlConnection } from 'types/core.types';
import { removeGraphConnections } from 'utils/graphql.utils';

interface IQuestionnaireSelectionProps {
  defaultSelectedQuestionnaireId?: string | undefined;
  size?: 'small' | 'medium';
  disabled?: boolean;
  onSelect: (questionnaire: IBaseQuestionnaire | null) => void;
}

const StyledAutocomplete = styled(Autocomplete<IBaseQuestionnaire>)({
  '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"]':
    {
      padding: 4,
      '& .MuiAutocomplete-input': {
        fontSize: 12,
      },
    },
});

const QuestionnaireSelection: FC<IQuestionnaireSelectionProps> = ({
  defaultSelectedQuestionnaireId,
  size = 'medium',
  disabled,
  onSelect,
}) => {
  const { data, loading, refetch } = useQuery<{
    questionnaires: IGraphQlConnection<IQuestionnaire>;
  }>(GET_QUESTIONNAIRES);
  const { openDialog } = useDialog();

  // State for selected questionnaire object (not just id)
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<IBaseQuestionnaire | null>(
    null
  );

  const questionnaires = useMemo(
    () => (data?.questionnaires ? removeGraphConnections(data.questionnaires) : []),
    [data?.questionnaires]
  );

  const handleSelectionChange = useCallback(
    (_: unknown, newSelectedQuestionnaire: IBaseQuestionnaire | null) => {
      setSelectedQuestionnaire(newSelectedQuestionnaire);
      onSelect(newSelectedQuestionnaire);
    },
    [onSelect]
  );

  const handleNewQuestionnaireCreated = async (questionnaireId: string) => {
    await refetch();
    // After refetch, select the new questionnaire by id
    const newQ = questionnaires.find(q => q.id === questionnaireId);
    if (newQ) {
      setSelectedQuestionnaire(newQ);
      onSelect(newQ);
    } else {
      // fallback: will be handled by useEffect below
      setSelectedQuestionnaire(null);
    }
  };

  const handleAddNewItemClick = () => {
    openDialog({
      type: 'ADD_EDIT_QUESTIONNAIRE',
      props: {
        onQuestionnaireCreated: questionnaire => handleNewQuestionnaireCreated(questionnaire.id),
      },
    });
  };

  // Preselect questionnaire when data or defaultSelectedQuestionnaireId changes
  useEffect(() => {
    if (!questionnaires.length) return;
    if (defaultSelectedQuestionnaireId) {
      const found = questionnaires.find(q => q.id === defaultSelectedQuestionnaireId);
      if (found) {
        setSelectedQuestionnaire(found);
        onSelect(found);
      } else {
        setSelectedQuestionnaire(null);
        onSelect(null);
      }
    } else {
      setSelectedQuestionnaire(null);
      onSelect(null);
    }
    // Only run when questionnaires or defaultSelectedQuestionnaireId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaires, defaultSelectedQuestionnaireId]);

  return (
    <Box width="100%">
      {loading && (
        <Box position="relative" mt={5}>
          <Loader />
        </Box>
      )}

      <FormControl fullWidth size={size}>
        <StyledAutocomplete
          loading={loading}
          value={selectedQuestionnaire}
          options={questionnaires}
          fullWidth
          size={size}
          disabled={disabled}
          PaperComponent={({ children }) => (
            <PaperWithAddItem
              ActionComponent={
                <MenuItem onMouseDown={handleAddNewItemClick}>
                  <Icon name="plus" mr={1} />
                  Add new questionnaire
                </MenuItem>
              }
            >
              {children}
            </PaperWithAddItem>
          )}
          getOptionKey={option => option.id}
          getOptionLabel={option => (option ? `${option.title} - ${option.year}` : '')}
          renderOption={(props, option: IBaseQuestionnaire) => (
            <li {...props} key={option.id} value={option.id}>
              <Icon mr={2} name="document" />
              {option.title} - {option.year}
            </li>
          )}
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Type to select"
              onKeyPress={keyEvent => {
                if (keyEvent.key === 'Enter') {
                  keyEvent.preventDefault();
                }
              }}
              InputProps={{
                style: { height: size === 'medium' ? 40 : 30 },
                ...params.InputProps,
                startAdornment: <Icon name="document" />,
              }}
            />
          )}
          onChange={handleSelectionChange}
        />
      </FormControl>
    </Box>
  );
};

export default QuestionnaireSelection;
